import { Enduser, FormResponseValue } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { isSubmittableResponse, normalizeResponseForSubmission, substituteResponseFieldText } from "../../utils";
import type { LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";

export interface BuildResponsesToSubmitArgs {
  responsesAfterUpload: LocalTellescopeFormResponseMap;
  existingResponses: FormResponseValue[] | undefined;
  submittedFieldIds: Set<string>;
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
}

/**
 * Assembles the responses to submit: the fields the enduser actually navigated through, plus any
 * prior answers for fields they did not — so a partial re-submission does not overwrite earlier
 * answers with seeded blanks.
 *
 * Each response's title/description have their `{{enduser.*}}` tokens substituted, so the stored
 * record keeps the text the enduser read rather than the authored template.
 *
 * `Redirect` rows are dropped: legacy submitted them, but their empty seed fails the API's validator
 * and would reject the whole submission — see `isSubmittableResponse`. They stay in the substitution
 * context so token resolution still sees every answer.
 */
export const buildResponsesToSubmit = ({
  responsesAfterUpload,
  existingResponses,
  submittedFieldIds,
  enduser,
  fieldsMap,
}: BuildResponsesToSubmitArgs): FormResponseValue[] => {
  const navigatedResponses = Object.values(responsesAfterUpload).filter((r) => submittedFieldIds.has(r.fieldId));
  const priorResponses = (existingResponses ?? []).filter((r) => !submittedFieldIds.has(r.fieldId));

  const responses = [...navigatedResponses, ...priorResponses];

  return responses.reduce<FormResponseValue[]>((acc, r) => {
    if (isSubmittableResponse(r)) {
      acc.push(normalizeResponseForSubmission(substituteResponseFieldText(r, { enduser, responses, fieldsMap })));
    }
    return acc;
  }, []);
};
